import React, { Fragment, useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Filter } from 'Components/widgets/filter/Filter';
import { FiltersBarClearAllBtn } from './FiltersBarClearAllBtn';
type Props = {
  caption?: string;
  className?: string;
  dataTestid?: string;
  filters: Array<FilterItem>;
  hasClearAll?: boolean;
  hasForceClose?: boolean;
  isLarge?: boolean;
  onChange: FilterItemOnChangeFunc;
  onClear?: (name: string) => void;
  onClearAll?: () => void;
  onClick?: (name: string) => void;
  onClickOption?: (option: FilterOptionProps) => void;
  portalName: string;
};
export const FiltersBar = ({
  caption,
  className,
  dataTestid,
  hasClearAll = false,
  hasForceClose = false,
  isLarge,
  onChange,
  onClear,
  onClearAll,
  onClick,
  onClickOption,
  portalName,
  filters: propsFilters,
  ...rest
}: Props) => {
  const { isDesktop } = useResponsive();
  const [filters, setFilters] = useState(propsFilters ? [...propsFilters] : []);
  useEffect(() => {
    propsFilters && setFilters(propsFilters);
  }, [propsFilters]);

  const renderFilters = () =>
    filters.map((filter, index) => (
      <Filter
        className={classnames('flex-1', {
          'last:scroll-x-ghost-m-10 last:scroll-x-ghost-m':
            index === filters.length - 1 && !isDesktop,
        })}
        customRender={filter.customRender}
        customRenderParent={filter.customRenderParent}
        id={filter.id}
        key={filter.id}
        label={filter.label}
        name={filter.id}
        onChange={onChange}
        onClear={onClear}
        onClick={onClick}
        onClickOption={onClickOption}
        hasForceClose={hasForceClose}
        options={filter.options}
        portalName={portalName}
        setOptions={(newOptions) => {
          const newFilters = [...filters];
          newFilters[index] = { ...newFilters[index], options: newOptions };
          setFilters(newFilters);
        }}
      />
    ));

  const clearFiltersAndSelectedOptions = () => {
    const newFilters = filters.map((filter) => ({
      ...filter,
      options: filter.options.map((opt) => ({ ...opt, isSelected: false })),
    }));
    setFilters([...newFilters]);
    onClearAll?.();
  };

  return (
    <Fragment>
      <div
        className={classnames({
          'animation-duration-500 animate-slideInTop border-b-1 border-t-1 border-grey bg-white':
            isLarge,
        })}
      >
        <SiteContainer isWide={!isDesktop}>
          <div
            {...rest}
            className={classnames(
              'flex w-full flex-wrap items-center justify-between gap-10 bg-white p-10',
              {
                'border-1 border-grey shadow-diffuse': !isLarge,
              },
              className,
            )}
            data-testid={dataTestid}
          >
            {caption && (
              <p className="w-1/2 text-12 font-bold md:text-14 lg:w-auto lg:text-16">{caption}</p>
            )}
            {hasClearAll && (
              <FiltersBarClearAllBtn
                className="shrink lg:order-last"
                onClick={clearFiltersAndSelectedOptions}
              />
            )}
            {isDesktop ? (
              renderFilters()
            ) : (
              <Scrollable className="-mx-10" childrenWrapperClassNames="flex gap-10 px-10">
                {renderFilters()}
              </Scrollable>
            )}
          </div>
        </SiteContainer>
      </div>
      <div id={portalName} />
    </Fragment>
  );
};
